import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import type {AttendanceSummary} from '@/entities/me/types';
import {StatTile} from '@/shared/ui/stat-tile';
import {AppText} from '@/shared/ui/text';
import {fonts} from '@/shared/ui/fonts';
import {radii, spacing, useTheme} from '@/shared/ui/theme';
import {formatHoursFromMinutes} from '@/shared/lib/format';

/**
 * The month at a glance: worked time up top (the number that matters most, so it gets
 * the full width and the 40pt figure), then the four day-counters that qualify it.
 * Every figure is IBM Plex Mono with tabular figures — a column of counts must line up
 * digit-for-digit, the same rule the console's tables follow.
 *
 * The headline is the shared `StatTile` now rather than a card with a private
 * `fontSize: 30` in it. The four counters below stay local: they are a wrapped grid of
 * four small washes, not four tiles, and a tile at that size would be a box with two
 * words and no room for either.
 *
 * The counters are now WASHED IN THEIR OWN MEANING rather than all sitting on white:
 * late and overtime days in amber, absences in red, and — the part that matters — a
 * zero count stays neutral grey. A month with nothing wrong on it shows no colour at
 * all, so any colour at all is the thing to look at.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  grid: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2)},
  counter: {
    flexGrow: 1,
    flexBasis: '22%',
    minWidth: 76,
    borderRadius: radii.md,
    paddingHorizontal: spacing(3),
    paddingVertical: spacing(3),
    gap: 2,
  },
  counterFigure: {fontFamily: fonts.mono.medium, fontSize: 19, lineHeight: 26},
});

/** One day-counter. `tone` is null when the count is zero — see the note above. */
function Counter({
  value,
  label,
  fg,
  bg,
}: {
  value: string;
  label: string;
  fg: string;
  bg: string;
}) {
  return (
    <View style={[styles.counter, {backgroundColor: bg}]}>
      <AppText style={styles.counterFigure} color={fg}>
        {value}
      </AppText>
      <AppText variant="micro" color={fg} numberOfLines={1}>
        {label}
      </AppText>
    </View>
  );
}

export function SummaryTiles({summary}: {summary: AttendanceSummary}) {
  const {t} = useTranslation();
  const p = useTheme();

  const worked = formatHoursFromMinutes(
    summary.total_worked_minutes,
    t('attendance.units.hour'),
    t('attendance.units.minute'),
  );

  const quiet = {fg: p.mutedForeground, bg: p.muted};
  const warn = {fg: p.warningInk, bg: p.warningSoft};
  const bad = {fg: p.destructiveInk, bg: p.destructiveSoft};

  return (
    <View style={styles.root}>
      <StatTile
        label={t('attendance.summary.worked')}
        value={worked}
        icon="clock"
        tone="info"
      />

      <View style={styles.grid}>
        <Counter
          value={`${summary.present_days}/${summary.scheduled_days}`}
          label={t('attendance.summary.present')}
          // Present-vs-scheduled is the neutral fact the other three qualify, so it
          // takes the plain foreground on the same grey wash as an all-clear counter.
          fg={p.foreground}
          bg={p.muted}
        />
        <Counter
          value={String(summary.late_days)}
          label={t('attendance.summary.late')}
          {...(summary.late_days > 0 ? warn : quiet)}
        />
        <Counter
          value={String(summary.overtime_days)}
          label={t('attendance.summary.overtime')}
          {...(summary.overtime_days > 0 ? warn : quiet)}
        />
        <Counter
          value={String(summary.absent_days)}
          label={t('attendance.summary.absent')}
          {...(summary.absent_days > 0 ? bad : quiet)}
        />
      </View>
    </View>
  );
}
